import type { ChargedAttack } from '../../../data/full/echassier';
import { angleOf } from '../../../shared/angle';
import { isBeatTick } from '../../../shared/tempo';
import { fan } from '../../attacks/fan';
import { fireShot, type Shot } from '../../attacks/shot';
import { pullTrigger } from '../../attacks/trigger';
import type { AttackModule, Upgraded } from '../../attacks/types';
import type { PlayerState } from '../../state';
import { statValue } from '../../stats';

// Half a beat of charge, the bot's stop and go, kills more of a sparse crowd than a full charge.
const BOT_LET_GO = 0.5;

// Standing still charges the shot. His first step lets it go; at full charge it leaves on its own on
// the beat; walking, an uncharged shot leaves on each beat. With nothing to shoot, a step loses it.
export const charged: AttackModule<ChargedAttack> = {
  reads: [
    'attackCooldownMul',
    'damageMul',
    'pierceAdd',
    'projectileCountAdd',
    'projectileSpeedMul',
  ],

  step({ state, tempo }, player, attack, input) {
    const still = input.move.x === 0 && input.move.y === 0;
    const full = fullChargeTicks(player, attack);
    if (still) {
      player.chargeTicks = Math.min(full, (player.chargeTicks ?? 0) + 1);
    } else if (!input.fire) {
      delete player.chargeTicks;
    }
    const held = player.chargeTicks;
    const beat = isBeatTick(state.tick, tempo);
    const pulled = input.fire && (still ? held === full && beat : held !== undefined || beat);
    if (!pullTrigger(player, pulled, attack.cooldownTicks)) {
      return;
    }
    delete player.chargeTicks;
    const charge = (held ?? 0) / full;
    const shot: Shot = {
      speed: statValue(player, 'projectileSpeedMul', attack.projectileSpeed),
      radius: attack.projectileRadius,
      damage: statValue(
        player,
        'damageMul',
        attack.damage + (attack.fullDamage - attack.damage) * charge,
      ),
      ticks: attack.rangeTicks,
      pierce: statValue(
        player,
        'pierceAdd',
        Math.floor(attack.pierce + (attack.fullPierce - attack.pierce) * charge),
      ),
    };
    const count = Math.max(1, Math.floor(statValue(player, 'projectileCountAdd', 1)));
    for (const way of fan(player.aim, count, attack.gapRadians * (count - 1))) {
      fireShot(state, player, way, shot);
    }
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  letGo(player, attack) {
    return player.attackCooldown <= 0 && chargeShare(player, attack) >= BOT_LET_GO;
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', attack.projectileSpeed) * attack.rangeTicks;
  },
};

// A faster cooldown charges faster too.
function fullChargeTicks(player: Upgraded, attack: ChargedAttack): number {
  return Math.max(1, Math.ceil(statValue(player, 'attackCooldownMul', attack.chargeTicks)));
}

// From 0, nothing held, to 1, full: the render lights a button per bad vibe it will cross.
export function chargeShare(
  player: Upgraded & Pick<PlayerState, 'chargeTicks'>,
  attack: ChargedAttack,
): number {
  return (player.chargeTicks ?? 0) / fullChargeTicks(player, attack);
}
